import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { User, Mail, Briefcase, Lock, Save, Eye, EyeOff, Camera, X, Bell, CheckCircle, XCircle, Wallet, TrendingUp, TrendingDown, Loader2, Filter, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import PushNotificationManager from '@/components/PushNotificationManager';
import SelfieCapture from '@/components/SelfieCapture';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(user?.profile_photo || null);
  const [selfieDialogOpen, setSelfieDialogOpen] = useState(false);
  const [photoRegistered, setPhotoRegistered] = useState(false);
  const [loadingPhotoStatus, setLoadingPhotoStatus] = useState(true);
  
  // My Ledger state
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [ledgerFilter, setLedgerFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('profile');
  
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    mobile_number: user?.mobile_number || '',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // Check if user has registered photo
  useEffect(() => {
    const checkPhotoStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}/hr/employees/${user?.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPhotoRegistered(!!response.data?.selfie_registered);
      } catch (error) {
        setPhotoRegistered(false);
      } finally {
        setLoadingPhotoStatus(false);
      }
    };

    if (user?.id) {
      checkPhotoStatus();
    }
  }, [user?.id]);

  // Fetch ledger when My Ledger tab is selected
  useEffect(() => {
    if (activeTab === 'ledger' && user?.id) {
      fetchMyLedger();
    }
  }, [activeTab, user?.id]);

  const fetchMyLedger = async () => {
    setLoadingLedger(true);
    try {
      const token = localStorage.getItem('token');
      
      // First, sync any missing incentives to the ledger
      await axios.post(`${API_BASE_URL}/hr/ledger/${user?.id}/sync-incentives`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {}); // Ignore errors from sync
      
      // Then fetch the ledger
      const response = await axios.get(`${API_BASE_URL}/hr/ledger/${user?.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedgerEntries(response.data.entries || []);
      setLedgerSummary({
        total_credits: response.data.total_credits || 0,
        total_debits: response.data.total_debits || 0,
        net_balance: response.data.net_balance || 0
      });
    } catch (error) {
      console.error('Error fetching ledger:', error);
      setLedgerEntries([]);
      setLedgerSummary(null);
    } finally {
      setLoadingLedger(false);
    }
  };

  const getFilteredLedgerEntries = () => {
    if (ledgerFilter === 'all') return ledgerEntries;
    return ledgerEntries.filter(entry => entry.entry_type === ledgerFilter);
  };

  const getLedgerEntryStyle = (entryType) => {
    const styles = {
      salary: { bg: 'bg-green-500/10', text: 'text-green-400', label: 'Salary' },
      incentive: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', label: 'Incentive' },
      advance: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Advance' },
      loan: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'Loan' },
      penalty: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Penalty' },
      leave_deduction: { bg: 'bg-orange-500/10', text: 'text-orange-400', label: 'Leave Deduction' },
      adjustment: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Adjustment' },
      bonus: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Bonus' }
    };
    return styles[entryType] || { bg: 'bg-gray-500/10', text: 'text-gray-400', label: entryType };
  };

  const handlePhotoRegistrationSuccess = () => {
    setPhotoRegistered(true);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/auth/profile`,
        {
          ...profileData,
          profile_photo: profilePhoto,
          mobile_number: profileData.mobile_number,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const updatedUser = { ...response.data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      toast.success('Profile updated successfully');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setProfilePhoto(null);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/auth/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success('Password changed successfully. Please login again.');
      setTimeout(() => {
        logout();
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-playfair text-4xl font-bold text-wt-text-primary mb-2">
          My Account
        </h1>
        <p className="text-wt-text-secondary">Manage your profile, security, and view your financial ledger</p>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-wt-subtle border border-wt-border p-1 rounded-lg">
          <TabsTrigger 
            value="profile" 
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-wt-bg px-6 py-2"
          >
            <User className="h-4 w-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger 
            value="ledger" 
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-wt-bg px-6 py-2"
          >
            <Wallet className="h-4 w-4 mr-2" />
            My Ledger
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab Content */}
        <TabsContent value="profile" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-lg bg-wt-gold/10 border border-wt-gold/20">
                  <User className="h-6 w-6 text-wt-gold" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-wt-text-primary">Profile Information</h2>
                  <p className="text-sm text-wt-text-secondary">Update your personal details</p>
                </div>
              </div>

          <form onSubmit={handleProfileUpdate} className="space-y-4" data-testid="profile-form">
            <div className="flex flex-col items-center mb-6">
              <div className="relative group">
                <div className="h-32 w-32 rounded-full bg-wt-gold/20 flex items-center justify-center overflow-hidden border-4 border-wt-border">
                  {profilePhoto ? (
                    <img
                      src={profilePhoto}
                      alt="Profile"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-4xl font-bold text-wt-gold">
                      {user?.full_name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <label
                  htmlFor="photo-upload"
                  className="absolute bottom-0 right-0 p-2 rounded-full bg-wt-gold hover:bg-wt-gold-hover cursor-pointer smooth-transition"
                >
                  <Camera className="h-5 w-5 text-black" />
                  <input
                    id="photo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    data-testid="photo-upload-input"
                  />
                </label>
                {profilePhoto && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="absolute top-0 right-0 p-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white smooth-transition"
                    data-testid="remove-photo-button"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-wt-text-muted mt-2">
                Click camera icon to upload photo (Max 5MB)
              </p>
            </div>

            <div>
              <Label htmlFor="full_name" className="text-wt-text-primary">
                Full Name
              </Label>
              <Input
                id="full_name"
                value={profileData.full_name}
                onChange={(e) =>
                  setProfileData({ ...profileData, full_name: e.target.value })
                }
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-2"
                data-testid="full-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-wt-text-primary">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={profileData.email}
                disabled
                className="bg-wt-subtle border-wt-border text-wt-text-muted mt-2 cursor-not-allowed"
              />
              <p className="text-xs text-wt-text-muted mt-1">Email cannot be changed</p>
            </div>

            <div>
              <Label htmlFor="mobile_number" className="text-wt-text-primary">
                Mobile Number
              </Label>
              <Input
                id="mobile_number"
                type="tel"
                value={profileData.mobile_number}
                onChange={(e) =>
                  setProfileData({ ...profileData, mobile_number: e.target.value })
                }
                placeholder="+971501234567"
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-2"
                data-testid="mobile-number-input"
              />
              <p className="text-xs text-wt-text-muted mt-1">Used for WhatsApp & call buttons on tracking page</p>
            </div>

            <div>
              <Label className="text-wt-text-primary">Role</Label>
              <div className="mt-2 px-3 py-2 rounded-lg bg-wt-subtle border border-wt-border">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-wt-gold" />
                  <span className="text-wt-text-primary capitalize font-medium">
                    {user?.role}
                  </span>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-wt-gold text-black hover:bg-wt-gold-hover font-semibold"
              data-testid="save-profile-button"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <Lock className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Change Password</h2>
              <p className="text-sm text-wt-text-secondary">Update your account password</p>
            </div>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4" data-testid="password-form">
            <div>
              <Label htmlFor="current_password" className="text-wt-text-primary">
                Current Password
              </Label>
              <div className="relative mt-2">
                <Input
                  id="current_password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.current_password}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, current_password: e.target.value })
                  }
                  className="bg-wt-bg border-wt-border text-wt-text-primary pr-12"
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-wt-text-muted hover:text-wt-text-primary"
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="new_password" className="text-wt-text-primary">
                New Password
              </Label>
              <div className="relative mt-2">
                <Input
                  id="new_password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, new_password: e.target.value })
                  }
                  className="bg-wt-bg border-wt-border text-wt-text-primary pr-12"
                  required
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-wt-text-muted hover:text-wt-text-primary"
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirm_password" className="text-wt-text-primary">
                Confirm New Password
              </Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, confirm_password: e.target.value })
                }
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-2"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <p className="text-xs text-blue-400">
                Password must be at least 6 characters long. You will be logged out after changing your password.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-500 text-white hover:bg-blue-600 font-semibold"
              data-testid="change-password-button"
            >
              <Lock className="h-4 w-4 mr-2" />
              Change Password
            </Button>
          </form>
        </motion.div>

        {/* Push Notifications Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-wt-gold/10 border border-wt-gold/20">
              <Bell className="h-6 w-6 text-wt-gold" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Push Notifications</h2>
              <p className="text-sm text-wt-text-secondary">Get notified about important updates</p>
            </div>
          </div>

          <PushNotificationManager />

          <div className="mt-4 p-3 bg-wt-subtle rounded-lg border border-wt-border">
            <p className="text-xs text-wt-text-muted">
              Push notifications will alert you about:
            </p>
            <ul className="mt-2 space-y-1 text-xs text-wt-text-secondary">
              <li>• New order assignments (for drivers)</li>
              <li>• New lead assignments (for sales)</li>
              <li>• Lead follow-up reminders</li>
              <li>• Cash handover approvals</li>
            </ul>
          </div>
        </motion.div>

        {/* Photo Registration Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <Camera className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Attendance Photo</h2>
              <p className="text-sm text-wt-text-secondary">Register your photo for attendance</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Status */}
            <div className={`flex items-center gap-3 p-4 rounded-lg border ${
              photoRegistered 
                ? 'bg-green-500/10 border-green-500/20' 
                : 'bg-yellow-500/10 border-yellow-500/20'
            }`}>
              {loadingPhotoStatus ? (
                <div className="flex items-center gap-2 text-wt-text-muted">
                  <div className="h-5 w-5 border-2 border-wt-text-muted border-t-transparent rounded-full animate-spin" />
                  <span>Checking photo status...</span>
                </div>
              ) : photoRegistered ? (
                <>
                  <CheckCircle className="h-6 w-6 text-green-400" />
                  <div>
                    <p className="font-medium text-green-400">Photo Registered</p>
                    <p className="text-sm text-wt-text-muted">You can mark attendance with selfie</p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="h-6 w-6 text-yellow-400" />
                  <div>
                    <p className="font-medium text-yellow-400">Photo Not Registered</p>
                    <p className="text-sm text-wt-text-muted">Register your photo to enable selfie attendance</p>
                  </div>
                </>
              )}
            </div>

            {/* Info */}
            <div className="p-3 bg-wt-subtle rounded-lg border border-wt-border">
              <p className="text-xs text-wt-text-muted mb-2">
                Photo registration allows you to:
              </p>
              <ul className="space-y-1 text-xs text-wt-text-secondary">
                <li>• Mark attendance with a selfie</li>
                <li>• Quick and easy check-in process</li>
                <li>• Visual verification for admin</li>
              </ul>
            </div>

            {/* Action Button */}
            <Button
              onClick={() => setSelfieDialogOpen(true)}
              className={photoRegistered 
                ? "bg-purple-500 hover:bg-purple-600 text-white w-full" 
                : "bg-wt-gold hover:bg-wt-gold-hover text-black w-full"
              }
              data-testid="register-photo-btn"
            >
              <Camera className="h-4 w-4 mr-2" />
              {photoRegistered ? 'Update Photo' : 'Register Photo'}
            </Button>
          </div>
        </motion.div>
          </div>
        </TabsContent>

        {/* My Ledger Tab Content */}
        <TabsContent value="ledger" className="mt-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {loadingLedger ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                {ledgerSummary && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-wt-paper border border-wt-border rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-green-500/10">
                          <TrendingUp className="h-5 w-5 text-green-400" />
                        </div>
                        <p className="text-wt-text-muted">Total Earned</p>
                      </div>
                      <p className="text-2xl font-bold text-green-400">
                        {ledgerSummary.total_credits?.toLocaleString()} AED
                      </p>
                    </div>
                    <div className="bg-wt-paper border border-wt-border rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-red-500/10">
                          <TrendingDown className="h-5 w-5 text-red-400" />
                        </div>
                        <p className="text-wt-text-muted">Total Deductions</p>
                      </div>
                      <p className="text-2xl font-bold text-red-400">
                        {ledgerSummary.total_debits?.toLocaleString()} AED
                      </p>
                    </div>
                    <div className="bg-wt-paper border border-wt-border rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`p-2 rounded-lg ${ledgerSummary.net_balance >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                          <Wallet className={`h-5 w-5 ${ledgerSummary.net_balance >= 0 ? 'text-green-400' : 'text-red-400'}`} />
                        </div>
                        <p className="text-wt-text-muted">Net Balance</p>
                      </div>
                      <p className={`text-2xl font-bold ${ledgerSummary.net_balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {ledgerSummary.net_balance?.toLocaleString()} AED
                      </p>
                    </div>
                  </div>
                )}

                {/* Filter */}
                <div className="bg-wt-paper border border-wt-border rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <Filter className="h-4 w-4 text-wt-text-muted" />
                    <Select value={ledgerFilter} onValueChange={setLedgerFilter}>
                      <SelectTrigger className="w-[200px] bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue placeholder="Filter by type" />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-paper border-wt-border">
                        <SelectItem value="all">All Transactions</SelectItem>
                        <SelectItem value="salary">Salary</SelectItem>
                        <SelectItem value="incentive">Incentive</SelectItem>
                        <SelectItem value="bonus">Bonus</SelectItem>
                        <SelectItem value="advance">Advance</SelectItem>
                        <SelectItem value="penalty">Penalty</SelectItem>
                        <SelectItem value="leave_deduction">Leave Deduction</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Ledger Table */}
                <div className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-wt-subtle">
                      <tr>
                        <th className="text-left p-4 text-wt-text-muted text-sm">Date</th>
                        <th className="text-left p-4 text-wt-text-muted text-sm">Type</th>
                        <th className="text-left p-4 text-wt-text-muted text-sm">Description</th>
                        <th className="text-right p-4 text-wt-text-muted text-sm">Credit</th>
                        <th className="text-right p-4 text-wt-text-muted text-sm">Debit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredLedgerEntries().length > 0 ? (
                        getFilteredLedgerEntries().map((entry, index) => {
                          const style = getLedgerEntryStyle(entry.entry_type);
                          const isCredit = entry.amount > 0;
                          return (
                            <tr key={entry.id || index} className="border-t border-wt-border/50 hover:bg-wt-subtle/30">
                              <td className="p-4 text-wt-text-primary text-sm">
                                {new Date(entry.created_at).toLocaleDateString()}
                              </td>
                              <td className="p-4">
                                <span className={`px-3 py-1 rounded text-xs ${style.bg} ${style.text}`}>
                                  {style.label}
                                </span>
                              </td>
                              <td className="p-4 text-wt-text-primary text-sm">
                                <div className="max-w-[300px]">
                                  <div className="truncate">{entry.description}</div>
                                  {entry.reference_month && (
                                    <div className="text-xs text-wt-text-muted">Month: {entry.reference_month}</div>
                                  )}
                                </div>
                              </td>
                              <td className="p-4 text-right">
                                {isCredit && (
                                  <span className="text-green-400 font-semibold">
                                    +{Math.abs(entry.amount).toLocaleString()} AED
                                  </span>
                                )}
                              </td>
                              <td className="p-4 text-right">
                                {!isCredit && (
                                  <span className="text-red-400 font-semibold">
                                    -{Math.abs(entry.amount).toLocaleString()} AED
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={5} className="p-12 text-center text-wt-text-muted">
                            <Wallet className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p>No ledger entries found</p>
                            <p className="text-sm mt-2">Your salary, incentives, and deductions will appear here</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Selfie Capture Dialog */}
      <SelfieCapture
        userId={user?.id}
        userName={user?.full_name}
        open={selfieDialogOpen}
        onOpenChange={setSelfieDialogOpen}
        onSuccess={handlePhotoRegistrationSuccess}
        mode="register"
      />
    </div>
  );
};

export default ProfilePage;